import { Component, EventEmitter, Inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { cloneDeep } from 'lodash';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { ScopeSelectionComponent } from '@src/app/components/scope-selection/scope-selection.component';
import { Scope } from '@ess/jg-rule-executor';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { RetailerScope } from '@src/app/model/retailer-scope';
import { Subject, takeUntil } from 'rxjs';
import { get, set } from 'lodash';

export type JsonModalResult = { body: JSON, scopes: Scope[] };

@Component({
  selector: 'app-json-modal',
  templateUrl: './json-modal.component.html',
  styleUrls: ['./json-modal.component.scss'],
  standalone: false,
})
export class JsonModalComponent implements OnInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  public waitingForResponse: boolean = false;
  public submittedAtLeastOnce: boolean = false;
  public headerText: string;
  public submitText: string;
  public readOnly: boolean = false;
  public editorOptions: JsonEditorOptions;
  public scopeSelectionTypes: string[] = Object.keys(ScopeSelectionType);
  public scopeSelectionEnum = ScopeSelectionType;
  public chosenScopeSelectionType: ScopeSelectionType;
  public submit = new EventEmitter<JsonModalResult>();
  public scopes: RetailerScope[] = [];
  public subjectTitle: string;
  public subjectName: string;

  readonly editor = viewChild(JsonEditorComponent);
  readonly scopeSelection = viewChild(ScopeSelectionComponent);

  public jsonForm: FormGroup;

  constructor(
    private _dialogRef: MatDialogRef<JsonModalComponent>,
    private _formBuilder: FormBuilder,
    private _scopeService: ScopeService,
    @Inject(MAT_DIALOG_DATA) private _data: JsonModalDataObject
  ) {
    this.jsonForm = this._formBuilder.group({
      json: ['', [Validators.required]],
    });
    if (this._data.isScoped) {
      this._scopeService.scopes$
        .pipe(takeUntil(this._componentDestroyed$))
        .subscribe(scopes => this.scopes = scopes);
    }
  }

  public changeSelectionType(selection: string) {
    this.chosenScopeSelectionType = ScopeSelectionType[selection];
  }

  // convenience getter for easy access to form fields
  get f(): FormGroup['controls'] {
    return this.jsonForm.controls;
  }

  // on init, create json to fill input with
  public ngOnInit(): void {
    const body: object = {};

    this.editorOptions = new JsonEditorOptions();
    // set the open mode to text to prevent js errors: see https://github.com/mariohmol/ang-jsoneditor/issues/90
    this.editorOptions.mode = 'text';
    this.editorOptions.modes = ['text', 'tree', 'view']; // set all allowed modes

    // if we have an array of inputs, we need a different handling of skipFields
    if (Array.isArray(this._data.input)) {
      body[this._data.name] = this._data.input.map(
        input => this._skipFields(input.toGqlInput(), this._data.skipFields)
      );
    } else {
      body[this._data.name] = this._skipFields(this._data.input.toGqlInput(), this._data.skipFields);
    }

    if (this._data.readOnly) {
      this.readOnly = this._data.readOnly;
      this.editorOptions.modes = ['view'];
      this.editorOptions.mode = 'view';
    }

    this.f.json.setValue(body);
    this.headerText = this._data.headerText;
    this.submitText = this._data.submitText;
    this.subjectTitle = this._data.subjectTitle;
    this.subjectName = this._data.subjectName;
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }

  public closeDialog(): void {
    this._dialogRef.close();
  }

  /**
   * checks if valid JSON, and emits the parsed json if it is.
   */
  public onSubmit(): void {
    this.f.json.updateValueAndValidity();
    this.f.json.updateValueAndValidity(); // removes 'wrong' error from previous failed attempt

    this.submittedAtLeastOnce = true;
    this.waitingForResponse = true;

    if (!this.readOnly && this.editor().isValidJson()) {
      const valuesFilled: object | object[] = this.f.json.value[this._data.name];
      const body: object = {};

      // if we have an array of inputs, we need a different handling of skipFields
      if (Array.isArray(valuesFilled)) {
        body[this._data.name] = valuesFilled.map((value, index) => this._refillSkippedFields(
          value,
          this._data.input[index] as ToJsonInputTranslatable,
          this._data.skipFields
        ) as JSON);
      } else {
        body[this._data.name] = this._refillSkippedFields(
          valuesFilled,
          this._data.input as ToJsonInputTranslatable,
          this._data.skipFields
        ) as JSON;
      }

      const effectiveScopes: Scope[] = (
        this.chosenScopeSelectionType === ScopeSelectionType.pick
          ? this.scopeSelection().selectedScopes()
          : (this.chosenScopeSelectionType === ScopeSelectionType.all ? this.scopes : [])
      ).map(x => new Scope(x.id.toString()));

      this.submit.emit({ body: body as JSON, scopes: effectiveScopes });
    } else {
      this.waitingForResponse = false;
      window.alert('json is invalid');
    }
  }

  public isJsonString(jsonString: string): boolean {
    try {
      JSON.parse(jsonString);
    } catch (e) {
      return false;
    }
    return true;
  }

  /**
   * Sets values of "fields" in object to undefined
   *
   * @private
   */
  private _skipFields(dataObj: object, fields: string[]): object {
    const obj = cloneDeep(dataObj);
    if (fields?.length) {
      fields.forEach((field) => {
        if (get(obj, field) !== undefined) {
          set(obj, field, undefined);
        }
      });
    }
    return obj;
  }

  /**
   * Sets values for "fields" in filledObj back to original value
   * Allows full specification of field (say, 'a.b.c[0]') thanks to lodash get/set
   *
   * @private
   */
  private _refillSkippedFields(filledObj: object, originalObj: ToJsonInputTranslatable, fields: string[]): object {
    const obj = cloneDeep(filledObj);
    if (fields?.length) {
      fields.forEach((field) => {
        if (get(originalObj?.toGqlInput(), field)) {
          set(obj, field, get(originalObj.toGqlInput(), field));
        }
      });
    }
    return obj;
  }
}

enum ScopeSelectionType { current = 'current', all = 'all', pick = 'pick' }
